import { useState } from 'react';
import type { CommentItem, Role } from '../types';
import { mediaUrl } from '../api/client';
import { ru } from '../i18n/ru';

interface Props {
  items: CommentItem[];
  role: Role;
  viewerUserId: string;
  editWindowSeconds: number;
  onDelete: (comment: CommentItem) => void;
  onEdit: (comment: CommentItem) => void;
  onReply: (comment: CommentItem) => void;
  onToggleReaction: (comment: CommentItem, emoji: string) => void;
}

const QUICK_EMOJI = ['👍', '❤️', '🔥', '😂', '😮', '😢'];

interface MenuState {
  comment: CommentItem;
  x: number;
  y: number;
}

function initial(name: string): string {
  const t = (name || '').trim();
  return t ? t[0].toUpperCase() : '?';
}

// Стабильный цвет аватарки по имени автора (как в чат-клиентах).
const AVATAR_HUES = [4, 28, 145, 200, 260, 320, 175, 48];
function avatarColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return `hsl(${AVATAR_HUES[h % AVATAR_HUES.length]}, 62%, 52%)`;
}

/**
 * Сервер отдаёт naive UTC (без суффикса Z). Без явного Z браузер считает такое
 * время локальным, и окно правки у московского пользователя «истекает» сразу.
 */
function parseUtc(iso: string): number {
  const normalized = /([Zz]|[+-]\d{2}:\d{2})$/.test(iso) ? iso : `${iso}Z`;
  return Date.parse(normalized);
}

function canEdit(c: CommentItem, viewerUserId: string, editWindowSeconds: number): boolean {
  if (c.author_user_id !== viewerUserId || c.deleted_at !== null) return false;
  return Date.now() - parseUtc(c.created_at) < editWindowSeconds * 1000;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function dayKey(iso: string): string {
  return iso.slice(0, 10);
}

function formatDay(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const y = new Date();
  y.setDate(today.getDate() - 1);
  if (dayKey(d.toISOString()) === dayKey(today.toISOString())) return ru.today;
  if (dayKey(d.toISOString()) === dayKey(y.toISOString())) return ru.yesterday;
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}

// ── Контекстное меню в стиле Telegram ──────────────────────────────
function MessageMenu({
  state,
  role,
  viewerUserId,
  editWindowSeconds,
  onReact,
  onReply,
  onEdit,
  onDelete,
  onClose,
}: {
  state: MenuState;
  role: Role;
  viewerUserId: string;
  editWindowSeconds: number;
  onReact: (c: CommentItem, emoji: string) => void;
  onReply: (c: CommentItem) => void;
  onEdit: (c: CommentItem) => void;
  onDelete: (c: CommentItem) => void;
  onClose: () => void;
}) {
  const c = state.comment;
  const W = 210;
  const left = Math.max(8, Math.min(state.x, window.innerWidth - W - 8));
  const top = Math.max(56, Math.min(state.y, window.innerHeight - 280));

  function copy() {
    if (c.text) void navigator.clipboard?.writeText(c.text).catch(() => {});
    onClose();
  }

  return (
    <div className="menu-overlay" onClick={onClose} onContextMenu={(e) => e.preventDefault()}>
      <div
        className="menu-pop"
        style={{ left, top, width: W }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="menu-reactions">
          {QUICK_EMOJI.map((e) => (
            <button
              key={e}
              type="button"
              className="menu-emoji"
              onClick={() => {
                onReact(c, e);
                onClose();
              }}
            >
              {e}
            </button>
          ))}
        </div>
        <div className="menu-actions">
          <button type="button" className="menu-item" onClick={() => { onReply(c); onClose(); }}>
            <span>{ru.reply}</span>
            <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
              <path d="M10 9V5l-7 7 7 7v-4c5 0 8 1.5 10 5 0-7-4-11-10-11z" fill="currentColor" />
            </svg>
          </button>
          {c.text && (
            <button type="button" className="menu-item" onClick={copy}>
              <span>{ru.copy}</span>
              <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
                <path
                  d="M9 9h9v9H9zM5 5h9v2M5 5v9h2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
          {canEdit(c, viewerUserId, editWindowSeconds) && (
            <button type="button" className="menu-item" onClick={() => { onEdit(c); onClose(); }}>
              <span>{ru.editButton}</span>
              <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
                <path
                  d="M4 20h4l10-10-4-4L4 16v4zM14 6l4 4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
          {role === 'admin' && c.deleted_at === null && (
            <button
              type="button"
              className="menu-item danger"
              onClick={() => { onDelete(c); onClose(); }}
            >
              <span>{ru.deleteButton}</span>
              <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
                <path
                  d="M6 7h12M9 7V5h6v2M7 7l1 12h8l1-12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function CommentsList({
  items,
  role,
  viewerUserId,
  editWindowSeconds,
  onDelete,
  onEdit,
  onReply,
  onToggleReaction,
}: Props) {
  const [menu, setMenu] = useState<MenuState | null>(null);

  if (items.length === 0) {
    return <div className="empty">{ru.emptyList}</div>;
  }

  function openMenu(comment: CommentItem, e: React.MouseEvent) {
    e.preventDefault();
    setMenu({ comment, x: e.clientX, y: e.clientY });
  }

  let prevDay = '';
  return (
    <>
      <ul className="comments">
        {items.map((c) => {
          const isDeleted = c.deleted_at !== null;
          const name = c.author_display_name;
          const curDay = dayKey(c.created_at);
          const showSep = curDay !== prevDay;
          prevDay = curDay;
          return (
            <li key={c.id} className="comment-row">
              {showSep && <div className="date-sep">{formatDay(c.created_at)}</div>}
              <div className={'comment' + (isDeleted ? ' comment-deleted' : '')}>
                <span
                  className="avatar"
                  style={{ background: c.is_channel_reply ? 'var(--accent)' : avatarColor(name) }}
                >
                  {initial(name)}
                </span>
                <div
                  className="bubble"
                  onClick={(e) => !isDeleted && openMenu(c, e)}
                  onContextMenu={(e) => !isDeleted && openMenu(c, e)}
                >
                  <div className="author">
                    {name}
                    {c.is_channel_reply && <span className="channel-badge">{ru.channelTag}</span>}
                  </div>

                  {c.reply_to && (
                    <div className="reply-quote">
                      <span className="reply-quote-author">{c.reply_to.author}</span>
                      <span className="reply-quote-text">
                        {c.reply_to.deleted ? ru.deletedComment : c.reply_to.text}
                      </span>
                    </div>
                  )}

                  {c.media_file_id && c.media_type === 'photo' && (
                    <img
                      className="comment-media"
                      src={mediaUrl(c.media_file_id)}
                      alt=""
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                  {c.media_file_id && c.media_type === 'video' && (
                    <video
                      className="comment-media"
                      src={mediaUrl(c.media_file_id)}
                      controls
                      preload="metadata"
                      playsInline
                    />
                  )}

                  {c.text && <div className="text">{c.text}</div>}

                  {c.reactions.length > 0 && (
                    <div className="reactions">
                      {c.reactions.map((r) => (
                        <button
                          key={r.emoji}
                          type="button"
                          className={'reaction-chip' + (r.reacted ? ' active' : '')}
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleReaction(c, r.emoji);
                          }}
                        >
                          <span>{r.emoji}</span>
                          <span className="reaction-count">{r.count}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="bubble-footer">
                    {isDeleted && <span className="deleted-mark">{ru.deleted}</span>}
                    {c.edited_at !== null && <span className="edited-mark">{ru.edited}</span>}
                    <span className="date">{formatTime(c.created_at)}</span>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {menu && (
        <MessageMenu
          state={menu}
          role={role}
          viewerUserId={viewerUserId}
          editWindowSeconds={editWindowSeconds}
          onReact={onToggleReaction}
          onReply={onReply}
          onEdit={onEdit}
          onDelete={onDelete}
          onClose={() => setMenu(null)}
        />
      )}
    </>
  );
}
